import { useEffect, useLayoutEffect, useRef } from 'react';
import type { EffectTimelineClip } from '../vfx/types';
import { captureVideoFrame } from '../vfx/frameUtils';
import { RenderGraph } from '../vfx/renderGraph';
import { VfxPreviewWorkerClient, vfxPreviewWorkerSupported } from '../vfx/vfxPreviewWorkerClient';

interface UseVfxPreviewOptions {
  enabled?: boolean;
  currentTime: number;
  /** When set, RAF tick reads time from ref (60fps transport) instead of React props. */
  currentTimeRef?: React.RefObject<number>;
  fps?: number;
  effects: EffectTimelineClip[];
  width?: number;
  height?: number;
}

const PREVIEW_MIN_INTERVAL_MS = 1000 / 24;

function buildEffectsStructureKey(effects: EffectTimelineClip[] | undefined): string {
  return JSON.stringify(
    (effects || []).map((c) => ({
      id: c.id,
      type: c.type,
      startTime: c.startTime,
      endTime: c.endTime,
      enabled: c.enabled,
    })),
  );
}

async function captureFrameBitmap(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
  width: number,
  height: number,
): Promise<ImageBitmap | null> {
  const w = Math.max(2, Math.round(width));
  const h = Math.max(2, Math.round(height));
  if (w < 2 || h < 2) return null;

  if (source instanceof HTMLVideoElement) {
    if (!source.videoWidth || source.videoHeight < 1) return null;
  }

  try {
    // Letterbox into target size (same as captureVideoFrame contain) — never stretch.
    const frame = captureVideoFrame(source, w, h, { fit: 'contain' });
    return await createImageBitmap(frame.canvas);
  } catch {
    try {
      return await createImageBitmap(source);
    } catch {
      return null;
    }
  }
}

/**
 * Applies the VFX render graph to a preview media element.
 * Uses an OffscreenCanvas worker when available; falls back to main-thread 2D.
 */
export function useVfxPreview(
  mediaRef: React.RefObject<HTMLVideoElement | HTMLImageElement | null>,
  options: UseVfxPreviewOptions,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const graphRef = useRef<RenderGraph | null>(null);
  const graphStructureKeyRef = useRef('');
  const frameIndexRef = useRef(0);
  const optionsRef = useRef(options);
  const compositingRef = useRef(false);
  const lastRenderMsRef = useRef(0);
  const workerClientRef = useRef<VfxPreviewWorkerClient | null>(null);
  const useWorkerRef = useRef(false);
  optionsRef.current = options;
  const effectsStructureKey = buildEffectsStructureKey(options.effects);

  useEffect(() => {
    graphRef.current = null;
    graphStructureKeyRef.current = '';
    frameIndexRef.current = 0;
    compositingRef.current = false;
    workerClientRef.current?.reset();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [effectsStructureKey]);

  const clearPreviewCanvas = (canvas: HTMLCanvasElement | null, w: number, h: number) => {
    if (!canvas || w < 2 || h < 2) return;
    if (useWorkerRef.current) {
      workerClientRef.current?.clearDisplay();
      return;
    }
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, w, h);
  };

  useLayoutEffect(() => {
    if (useWorkerRef.current) return undefined;
    const canvas = canvasRef.current;
    const w = Math.max(2, Math.round(options.width || 0));
    const h = Math.max(2, Math.round(options.height || 0));
    if (!canvas || w < 2 || h < 2) return;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
  }, [options.width, options.height]);

  useEffect(() => {
    // Main-thread preview avoids offscreen canvas sizing glitches (partial-frame display).
    useWorkerRef.current = false;
    return undefined;
  }, [options.enabled]);

  useEffect(() => {
    if (options.enabled === false || !(options.effects || []).length) {
      compositingRef.current = false;
      graphRef.current = null;
      const canvas = canvasRef.current;
      const w = Math.max(2, Math.round(options.width || canvas?.width || 0));
      const h = Math.max(2, Math.round(options.height || canvas?.height || 0));
      clearPreviewCanvas(canvas, w, h);
      workerClientRef.current?.reset();
      return undefined;
    }

    let rafId = 0;

    const tick = () => {
      const opts = optionsRef.current;
      const el = mediaRef.current;
      const canvas = canvasRef.current;
      if (!el || !canvas || opts.enabled === false) {
        compositingRef.current = false;
        return;
      }

      const w = Math.max(2, Math.round(opts.width || canvas.width || 0));
      const h = Math.max(2, Math.round(opts.height || canvas.height || 0));
      if (w < 2 || h < 2) {
        compositingRef.current = false;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const timelineTime = opts.currentTimeRef?.current ?? opts.currentTime;

      const active = (opts.effects || []).filter(
        (e) => e.enabled !== false
          && timelineTime >= e.startTime
          && timelineTime < e.endTime,
      );

      if (!active.length) {
        clearPreviewCanvas(canvas, w, h);
        compositingRef.current = false;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const now = performance.now();
      if (now - lastRenderMsRef.current >= PREVIEW_MIN_INTERVAL_MS) {
        lastRenderMsRef.current = now;

        if (useWorkerRef.current && workerClientRef.current?.isActive()) {
          captureFrameBitmap(el, w, h).then((bitmap) => {
            if (!bitmap || !useWorkerRef.current || !workerClientRef.current?.isActive()) {
              bitmap?.close();
              return;
            }
            compositingRef.current = true;
            workerClientRef.current.renderFrame({
              bitmap,
              width: w,
              height: h,
              currentTime: timelineTime,
              fps: opts.fps ?? 30,
              effects: opts.effects || [],
              effectsStructureKey: buildEffectsStructureKey(opts.effects),
            }).catch(() => {
              compositingRef.current = false;
            });
          });
        } else {
          if (canvas.width !== w) canvas.width = w;
          if (canvas.height !== h) canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            compositingRef.current = false;
            rafId = requestAnimationFrame(tick);
            return;
          }
          try {
            const input = captureVideoFrame(el, w, h);
            const structureKey = buildEffectsStructureKey(opts.effects);
            if (!graphRef.current || graphStructureKeyRef.current !== structureKey) {
              graphRef.current = RenderGraph.fromClips(opts.effects || []);
              graphStructureKeyRef.current = structureKey;
              frameIndexRef.current = 0;
              graphRef.current.resetHistory();
            } else {
              graphRef.current.syncFromClips(opts.effects || []);
            }
            const out = graphRef.current.renderFrame(
              input,
              timelineTime,
              frameIndexRef.current,
              { fps: opts.fps ?? 30, mode: 'preview' },
            );
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(out.canvas as CanvasImageSource, 0, 0, w, h);
            frameIndexRef.current += 1;
            compositingRef.current = true;
          } catch {
            ctx.clearRect(0, 0, w, h);
            compositingRef.current = false;
          }
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      compositingRef.current = false;
    };
  }, [mediaRef, options.enabled, effectsStructureKey, options.width, options.height, options.effects?.length]);

  return {
    canvasRef,
    reset: () => {
      graphRef.current?.resetHistory();
      frameIndexRef.current = 0;
      workerClientRef.current?.reset();
    },
  };
}
